'use client';

import React from 'react';
import Link from 'next/link';
import { Box, Typography } from '@mui/material';

import { useT } from '@/hooks';

interface FooterNavSectionProps {
  title: string;
  links: Array<{ name: string; href: string }>;
  onLinkClick?: (name: string) => void;
  columns?: number;
}

export const FooterNavSection = ({ title, links, onLinkClick, columns }: FooterNavSectionProps) => {
  const { t } = useT();
  const handleItemClick = (name: string, href: string) => (e: React.MouseEvent) => {
    if (onLinkClick && (name === 'How it Works' || href === '#')) {
      e.preventDefault();
      onLinkClick(name);
    }
  };

  const renderNavItem = (link: (typeof links)[0]) => {
    const isInternal = link.href.startsWith('/');

    return (
      <Link
        key={link.name}
        href={link.href}
        onClick={handleItemClick(link.name, link.href)}
        target={isInternal ? undefined : '_blank'}
        rel={isInternal ? undefined : 'noopener noreferrer'}
        style={{ textDecoration: 'none' }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            '&:hover': { color: 'primary.main', transform: 'translateX(4px)' },
            transition: 'all 0.2s ease-in-out',
            display: 'inline-block',
            width: 'fit-content',
          }}
        >
          {t(link.name)}
        </Typography>
      </Link>
    );
  };

  const filteredLinks = links.filter((link) => link.href && link.href !== '#');

  const renderedNavItemsList = filteredLinks.map(renderNavItem);

  return (
    <>
      <Typography
        variant="subtitle2"
        fontWeight="bold"
        sx={{
          mb: 2.5,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontSize: '0.75rem',
          color: 'text.primary',
        }}
      >
        {title === 'Quick Links'
          ? t('footer.quickLinks')
          : title === 'Support'
            ? t('footer.support')
            : title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: columns ? `repeat(${columns}, 1fr)` : '1fr',
          gridTemplateRows: columns
            ? `repeat(${Math.ceil(filteredLinks.length / columns)}, auto)`
            : 'none',
          gridAutoFlow: columns ? 'column' : 'row',
          gap: 1.5,
          columnGap: columns ? 6 : 1.5,
        }}
      >
        {renderedNavItemsList}
      </Box>
    </>
  );
};
